import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import { Favorite, EventSeat, EmojiEvents } from '@material-ui/icons';
import clsx from 'clsx';
import { ITournament, MatchStatus } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { IMatchView } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { ZoneInformation } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { PAGE_KIT } from 'components/Page/PageKit';
import { TOURNAMENT_ACCENT } from './tournamentFormat';

/**
 * A shuffle tournament (TOURNAMENT_PLAN.md D30): the standings (points, shared places, each round's result) and every
 * round's teams, healers, sit-outs, map and result. Shared by the app and the website (an identical copy).
 * Electron 8 (Chromium 80): no flexbox gap; grid gap or margins.
 */
const TEAM_COLORS = ['#42a5f5', '#ef5350'];
const MARK: Record<string, {label: string, color: string, title: string}> = {
  W: {label: 'W', color: '#66bb6a', title: 'Won: 1 point'},
  L: {label: 'L', color: '#ef5350', title: 'Lost'},
  D: {label: 'D', color: '#90a4ae', title: 'Draw: no points'},
  S: {label: '½', color: '#ffca28', title: 'Sat out: half a point'},
  M: {label: 'M', color: '#78909c', title: 'Missed the round (no check-in)'},
  '-': {label: '–', color: 'rgba(255,255,255,0.25)', title: 'Not played yet'},
};

const useStyles = makeStyles((theme) => ({
  wrap: { display: 'grid', gridRowGap: theme.spacing(2.5) },
  title: { fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: PAGE_KIT.faint, marginBottom: 8 },
  panel: { borderRadius: 14, background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', overflow: 'hidden' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem',
    '& th': { fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PAGE_KIT.faint, padding: '9px 10px', textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderBottom: PAGE_KIT.divider },
    '& td': { padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)' },
    '& tr:last-child td': { borderBottom: 'none' },
  },
  me: { background: `${TOURNAMENT_ACCENT}12` },
  rank: { display: 'inline-grid', placeItems: 'center', minWidth: 26, height: 26, borderRadius: 8, fontWeight: 900, background: 'rgba(255,255,255,0.05)' },
  rankTop: { color: '#1a1300', background: TOURNAMENT_ACCENT },
  player: { textAlign: 'left !important' as any, fontWeight: 700, color: '#fff' },
  mark: { display: 'inline-grid', placeItems: 'center', width: 24, height: 24, borderRadius: 7, fontSize: '0.74rem', fontWeight: 900 },
  points: { fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' },

  rounds: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gridGap: 12 },
  round: {
    '&&': { display: 'grid', justifyContent: 'stretch', alignItems: 'stretch' }, textAlign: 'left', borderRadius: 14, overflow: 'hidden',
    background: 'rgba(0, 0, 0, 0.25)', border: '1px solid rgba(255, 255, 255, 0.08)', transition: 'border-color 0.15s', '&:hover': { borderColor: 'rgba(255,255,255,0.22)' },
  },
  roundLive: { borderColor: `${TOURNAMENT_ACCENT}88` },
  roundHead: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', padding: '9px 12px', borderBottom: PAGE_KIT.divider },
  roundName: { fontSize: '0.86rem', fontWeight: 900, color: '#fff' },
  roundMeta: { fontSize: '0.72rem', color: PAGE_KIT.faint, marginTop: 1 },
  status: { height: 22, padding: '0 9px', borderRadius: 999, display: 'grid', placeItems: 'center', fontSize: '0.7rem', fontWeight: 800 },
  teams: { display: 'grid', gridTemplateColumns: '1fr 1fr' },
  team: { padding: '8px 12px 10px', borderTop: '3px solid transparent' },
  teamHead: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 },
  member: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', fontSize: '0.82rem', color: '#fff', padding: '2px 0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
  healer: { fontSize: 14, color: '#69f0ae' },
  out: { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gridColumnGap: 6, alignItems: 'center', padding: '7px 12px', borderTop: PAGE_KIT.divider, fontSize: '0.76rem', color: PAGE_KIT.muted, '& svg': { fontSize: 15, color: '#ffca28' } },
  empty: { padding: theme.spacing(3), textAlign: 'center', color: PAGE_KIT.faint, fontSize: '0.86rem' },
}));

/** a map's name from its id */
function mapName(id: number | null | undefined): string | null {
  if(id == null) return null;
  for(const key of Object.keys(ZoneInformation)){
    const zone = ZoneInformation[key];
    for(const mapKey of Object.keys(zone.mapInfo || {}))
      if(zone.mapInfo[mapKey].id == id)
        return zone.mapInfo[mapKey].name;
  }
  return null;
}

const STATUS: Record<string, {label: string, color: string}> = {
  Ready: {label: 'Checking in', color: TOURNAMENT_ACCENT},
  Live: {label: 'Live', color: '#ef5350'},
  Completed: {label: 'Done', color: '#90a4ae'},
};

export const ShuffleBoard: React.FC<{tournament: ITournament, matches: IMatchView[], onMatchClick?: ((match: IMatchView) => void) | null, me?: string | null}> =
  ({tournament: t, matches, onMatchClick, me}) => {
  const classes = useStyles();
  const standings = t.shuffleStandings || [];
  const total = t.shuffleRounds || Math.max(matches.length, 1);
  const names: Record<string, string> = {};
  standings.forEach((s) => names[String(s.user?._id)] = s.user?.displayname || 'Unknown');
  matches.forEach((m) => [...(m.a.members || []), ...(m.b.members || [])].forEach((u: any) => { if(u?._id) names[String(u._id)] = u.displayname || names[String(u._id)] || 'Unknown'; }));
  const rounds = matches.slice().sort((x, y) => x.round - y.round);
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className={classes.wrap}>
      <div>
        <div className={classes.title}>Standings</div>
        <div className={classes.panel}>
          {standings.length == 0 ? <div className={classes.empty}>The standings start with round 1.</div> :
            <table className={classes.table}>
              <thead><tr><th style={{width: 40}}>#</th><th style={{textAlign: 'left'}}>Player</th>{Array.from({length: total}, (_, i) => <th key={i}>R{i + 1}</th>)}<th>Points</th></tr></thead>
              <tbody>
                {standings.map((s) => {
                  const id = String(s.user?._id);
                  return (
                    <tr key={id} className={clsx({[classes.me]: me && id == me})} onMouseEnter={() => setHovered(id)} onMouseLeave={() => setHovered(null)}>
                      <td><span className={clsx(classes.rank, {[classes.rankTop]: s.place == 1 && s.points > 0})}>{s.place}</span></td>
                      <td className={classes.player}>{s.place == 1 && s.points > 0 && t.status == 'Completed' && <EmojiEvents style={{fontSize: 15, color: TOURNAMENT_ACCENT, verticalAlign: -3, marginRight: 4}} />}{names[id] || 'Unknown'}</td>
                      {Array.from({length: total}, (_, i) => {
                        const mark = MARK[s.rounds[i] || '-'] || MARK['-'];
                        return <td key={i}><span className={classes.mark} title={mark.title} style={{color: mark.color, background: `${mark.color}1f`}}>{mark.label}</span></td>;
                      })}
                      <td className={classes.points}>{s.points % 1 ? s.points.toFixed(1) : s.points}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>}
        </div>
      </div>

      <div>
        <div className={classes.title}>Rounds · {rounds.length} of {total}</div>
        {rounds.length == 0 && <div className={clsx(classes.panel, classes.empty)}>Round 1 opens when the tournament starts.</div>}
        <div className={classes.rounds}>
          {rounds.map((m) => {
            const status = STATUS[m.status] || STATUS.Ready;
            const map = mapName(m.current?.map ?? (m.games || [])[0]?.map ?? m.firstMap);
            const mode = m.current?.mode || (m.games || [])[0]?.mode || m.firstMode;
            const sides: Array<'a' | 'b'> = ['a', 'b'];
            return (
              <ButtonBase key={String(m._id)} component="div" role="button" className={clsx(classes.round, {[classes.roundLive]: m.status == MatchStatus.Live})}
                onClick={() => onMatchClick && onMatchClick(m)} disabled={!onMatchClick}>
                <div className={classes.roundHead}>
                  <div>
                    <div className={classes.roundName}>Round {m.round}</div>
                    <div className={classes.roundMeta}>{[mode, map].filter(Boolean).join(' · ') || (m.status == MatchStatus.Ready ? 'Checking in' : 'Map to be chosen')}</div>
                  </div>
                  <span className={classes.status} style={{color: status.color, background: `${status.color}22`}}>
                    {m.status == MatchStatus.Completed ? (m.winner ? `Team ${m.winner == 'a' ? 1 : 2} won` : 'Draw') : status.label}
                  </span>
                </div>
                <div className={classes.teams}>
                  {sides.map((side, i) => {
                    const won = m.status == MatchStatus.Completed && m.winner == side;
                    const healer = side == 'a' ? m.shuffle?.healerA : m.shuffle?.healerB;
                    return (
                      <div key={side} className={classes.team} style={{borderTopColor: TEAM_COLORS[i], background: won ? `${TEAM_COLORS[i]}14` : undefined}}>
                        <div className={classes.teamHead} style={{color: TEAM_COLORS[i]}}>
                          <span>Team {i + 1}{won ? ' · won' : ''}</span>
                          <span style={{color: PAGE_KIT.faint}}>{(m[side].members || []).length}</span>
                        </div>
                        {(m[side].members || []).map((u: any) => {
                          const id = String(u?._id);
                          return (
                            <div key={id} className={classes.member} style={{opacity: (m.shuffle?.missed || []).map(String).indexOf(id) != -1 ? 0.45 : 1, fontWeight: hovered == id || id == me ? 900 : 600, color: hovered == id ? TOURNAMENT_ACCENT : undefined}}>
                              <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{names[id] || u?.displayname || 'Unknown'}</span>
                              {healer && String(healer) == id ? <Favorite className={classes.healer} titleAccess="The team's healer this round" /> : <span />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                {(m.shuffle?.out || []).length > 0 &&
                  <div className={classes.out}><EventSeat /><span>Sitting out: {(m.shuffle?.out || []).map((id) => names[String(id)] || 'Unknown').join(', ')} (½ point)</span></div>}
              </ButtonBase>
            );
          })}
        </div>
      </div>
    </div>
  );
};
